import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { Button, Card, FormField, Input, PasswordInput, PasswordStrengthMeter, Stack, guardSubmit } from "@salesapp/ui";
import { orgSettingsApi } from "../lib/orgSettingsApi";
import { ApiError } from "../lib/api";
import "./pages.css";
import { ThemeToggle } from "../shell/ThemeToggle";

export function SetupPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [showResend, setShowResend] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Choose a password of at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await orgSettingsApi.setupPassword(token, password);
      setMessage("Password saved. You can now sign in.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "This setup link is invalid or has expired.");
      setShowResend(true);
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend(event: FormEvent) {
    event.preventDefault();
    setResendError(null);
    setResendMessage(null);
    setResending(true);
    try {
      await orgSettingsApi.resendSetupLink(resendEmail);
      setResendMessage("Check that inbox for a new setup link. It expires in 5 minutes and can only be used once.");
    } catch (err) {
      setResendError(err instanceof ApiError ? err.message : "Enter the email you used to sign up.");
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="login-page-frame">
      <ThemeToggle />
      <Card padding="lg" className="login-card">
        <Stack gap={5}>
          {message ? (
            <Stack gap={4}>
              <h1 className="login-brand-title">Set your password</h1>
              <p role="status">{message} <a href="/login">Sign in</a></p>
            </Stack>
          ) : (
            <form onSubmit={guardSubmit(onSubmit)}>
              <Stack gap={4}>
                <h1 className="login-brand-title">Set your password</h1>
                <p className="login-brand-subtitle">
                  Use the setup link from your email. Choose a password of at least 12 characters. The link can only be used once and expires after a while.
                </p>
                {error && <p role="alert" className="settings-error">{error}</p>}
                <PasswordInput
                  label="New password"
                  tooltip="At least 12 characters. For a stronger password, mix uppercase, lowercase, numbers, and symbols."
                  required
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <PasswordStrengthMeter password={password} minLength={12} />
                <Button type="submit" disabled={submitting || token.length < 16}>{submitting ? "Saving…" : "Save password"}</Button>
              </Stack>
            </form>
          )}
          {!message && (showResend || token.length < 16) && (
            <form onSubmit={guardSubmit(onResend)}>
              <Stack gap={4}>
                <p className="login-brand-subtitle">
                  Link expired or already used? Enter the same email you signed up with to get a new 5-minute link. You can request a new link up to 3 times an hour.
                </p>
                {resendError && <p role="alert" className="settings-error">{resendError}</p>}
                {resendMessage && <p role="status">{resendMessage}</p>}
                <FormField label="Work email" htmlFor="setup-resend-email" required>
                  <Input
                    id="setup-resend-email"
                    type="email"
                    autoComplete="email"
                    value={resendEmail}
                    onChange={(event) => setResendEmail(event.target.value)}
                    required
                  />
                </FormField>
                <Button type="submit" variant="secondary" disabled={resending || !resendEmail}>
                  {resending ? "Sending…" : "Send a new setup link"}
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Card>
    </div>
  );
}
